import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import CgDialog from 'modules/Common/ui/CgDialog';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { isPlanlessOrgType } from 'modules/Admin/utils/planDisplay';
// Deep imports only — the modules/Common/form/v2 BARREL statically reaches six
// customer feature modules through GoodsSelectField/SearchSelectField, three of
// which survive tree-shaking into the bundle and fail phase 3's isolation gate.
import Form from 'modules/Common/form/v2/Form';
import FormSection from 'modules/Common/form/v2/FormSection';
import FormField from 'modules/Common/form/v2/FormField';
import TextField from 'modules/Common/form/v2/fields/TextField';
import SelectField from 'modules/Common/form/v2/fields/SelectField';
import CheckboxField from 'modules/Common/form/v2/fields/CheckboxField';
import CountrySelectField from 'modules/Common/form/v2/fields/CountrySelectField';
import { AlertType, notify, notifyError } from 'modules/Common/ui/toast/api';
import { useAdminPlans } from '../hooks/useAdminPlans';
import { instanceLabel } from '../hooks/useAdminInstances';
import {
  EDITABLE_KEYS,
  ORGANIZATION_TYPES,
  REGULATIONS,
  createOrganizationSchema,
  editOrganizationSchema,
  emptyOrganization,
  organizationToForm,
  pruneEmpty,
} from '../config/organizationForm';

/**
 * Create and edit in one component — the fields are the same set, and splitting
 * them would mean two places to keep the conditional rules in step.
 *
 * Server field errors arrive through Form's built-in useServerIssues seam
 * (ref.setServerIssues), which maps `issues[]` onto fields. Every 400 from phase 2
 * that blames a field carries that array — schema rejections through ZodError and
 * hand-thrown ones through fieldValidationError() — so there is no second shape to
 * branch on. What does NOT match a field is toasted, never swallowed.
 */

// The three ways the API rejects a brand. They need different fixes, so flattening
// them into one message sends whoever hits it looking in the wrong place.
export const BRAND_ERROR_COPY = {
  CLERK_INSTANCE_UNKNOWN: 'You do not manage that brand.',
  CLERK_INSTANCE_NOT_CONFIGURED:
    'This deployment cannot create organizations in that brand — it holds no Clerk secret for it. This is a deploy-config problem, not something you can fix here.',
  CLERK_INSTANCE_REQUIRED: 'Choose which brand to create this organization in.',
};

export const brandErrorFor = (error) => {
  const issues = error?.response?.data?.issues ?? [];
  const brandIssue = issues.find((i) => i.path?.[0] === 'clerkInstance');
  return brandIssue ? (BRAND_ERROR_COPY[brandIssue.code] ?? brandIssue.message) : null;
};

export default function OrganizationFormDialog({
  mode,
  organization,
  instances,
  isMultiInstance,
  createMutation,
  updateMutation,
  onClose,
}) {
  const isEdit = mode === 'edit';
  const formRef = useRef(null);

  const defaultValues = useMemo(() => {
    if (isEdit) return organizationToForm(organization);
    const base = emptyOrganization('declarant');
    // Default to the deployment's own instance when it is one of the options —
    // the overwhelmingly common case is creating in the brand you are looking at.
    const own = instances.find((i) => i.isOwn) ?? instances[0];
    return { ...base, clerkInstance: isMultiInstance ? (own?.clerkInstance ?? '') : '' };
  }, [isEdit, organization, instances, isMultiInstance]);

  // Mirrored out of the form because three things outside the form's own fields
  // depend on it: which catalogue useAdminPlans fetches, whether EORI renders, and
  // whether the plan section exists at all.
  const [type, setType] = useState(defaultValues.type);
  const plansQuery = useAdminPlans(isEdit ? null : type);

  const planOptions = useMemo(
    () => (plansQuery.data?.plans ?? []).map((p) => ({ value: p._id, label: p.name })),
    [plansQuery.data],
  );

  const brandOptions = useMemo(
    () => instances.map((i) => ({ value: i.clerkInstance, label: instanceLabel(i) })),
    [instances],
  );

  const mutation = isEdit ? updateMutation : createMutation;

  // Stable identity so TypeWatcher's effect does not re-run every render.
  const handleTypeChange = useCallback((next) => setType(next), []);

  const handleSubmit = async (rawValues) => {
    // A verifier firm has no console-entered address or phone: its Annex §1.3 details come from
    // its own Admin at setup, and the API stores neither for it.
    const values = isVerifier
      ? Object.fromEntries(
          Object.entries(rawValues).filter(([k]) => k !== 'address' && k !== 'phoneNumber'),
        )
      : rawValues;
    try {
      if (isEdit) {
        // PATCH is `.strict()` and takes no `type`: changing it would orphan every
        // child record. Only the allowed keys go, and empty ones are pruned so
        // editing a phone number does not require re-entering the address.
        const payload = pruneEmpty(Object.fromEntries(EDITABLE_KEYS.map((k) => [k, values[k]])));
        await updateMutation.mutateAsync({ id: organization._id, payload });
      } else {
        const { planNeverExpires, planExpiresAt, planId, clerkInstance, ...rest } = values;
        const payload = {
          ...pruneEmpty(rest),
          type: values.type,
          ...(planId ? { planId } : {}),
          // null is meaningful here (never expires, D8) and must survive pruning,
          // so the plan window is assembled outside pruneEmpty.
          ...(planNeverExpires ? { planExpiresAt: null } : planExpiresAt ? { planExpiresAt } : {}),
          ...(isMultiInstance && clerkInstance ? { clerkInstance } : {}),
        };
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      // Field-level issues land on their fields. Whatever did not match is not
      // visible anywhere on the form, so it must be surfaced some other way — a
      // server error is never swallowed (useServerIssues D17f).
      const unmatched = formRef.current?.setServerIssues?.(err) ?? [];
      const hadIssues = (err?.response?.data?.issues?.length ?? 0) > 0;

      // A brand rejection DOES light up the field via issues[], but all three
      // cases arrive on the same path and need different prose — "you may not",
      // "we cannot" and "you must choose" have different fixes, and one message
      // for all three sends the operator looking in the wrong place. So the
      // distinguishing copy gets its own toast on top of the field error.
      const brandMessage = brandErrorFor(err);
      if (brandMessage) notify('Brand not available', brandMessage, AlertType.ERROR);
      else if (unmatched.length > 0 || !hadIssues) notifyError(err);
    }
  };

  const isDeclarant = type === 'declarant';
  // Consultants and verifier firms hold no plan.
  const isPlanless = isPlanlessOrgType(type);
  const isVerifier = type === 'verifier';

  return (
    <CgDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={isEdit ? 'Edit organization' : 'New organization'}
      description={
        isEdit
          ? 'Account type cannot be changed — it determines which records the organization owns.'
          : undefined
      }
      width={720}
      footer={
        <div className="flex justify-end gap-2">
          <CgRadixButton
            variant="secondary"
            label="Cancel"
            onClick={onClose}
          />
          <CgRadixButton
            variant="primary"
            type="submit"
            form="admin-organization-form"
            label={isEdit ? 'Save changes' : 'Create organization'}
            loading={mutation.isPending}
            data-testid="org-form-submit"
          />
        </div>
      }
    >
      <Form
        id="admin-organization-form"
        ref={formRef}
        schema={isEdit ? editOrganizationSchema : createOrganizationSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      >
        <FormSection title="Account">
          <FormField
            name="type"
            label="Account type"
            required
            colSpan={6}
          >
            <SelectField
              options={ORGANIZATION_TYPES}
              disabled={isEdit}
            />
          </FormField>

          {/* Brand is create-only and appears only on a multi-instance deployment.
              With exactly one creatable instance the API resolves it, so asking is
              a question with one answer. */}
          {!isEdit && isMultiInstance ? (
            <FormField
              name="clerkInstance"
              label="Brand"
              required
              colSpan={6}
            >
              <SelectField options={brandOptions} />
            </FormField>
          ) : null}

          <FormField
            name="name"
            label="Organization name"
            required
            colSpan={12}
          >
            <TextField placeholder="Acme Steel" />
          </FormField>

          <FormField
            name="contactEmail"
            label="Contact email"
            required={!isDeclarant}
            colSpan={6}
          >
            <TextField
              type="email"
              placeholder="ops@acme.example"
            />
          </FormField>

          <FormField
            name="contactName"
            label="Contact name"
            required={!isDeclarant}
            colSpan={6}
          >
            <TextField />
          </FormField>

          {isVerifier ? null : (
            <FormField
              name="phoneNumber"
              label="Phone number"
              required={type === 'consultant'}
              colSpan={6}
            >
              <TextField />
            </FormField>
          )}

          {/* Both are declarant-only; the API rejects either on anything else, so they
              are not rendered rather than rendered-and-ignored. */}
          {isDeclarant ? (
            <>
              <FormField
                name="EORI"
                label="EORI"
                colSpan={6}
              >
                <TextField placeholder="ES123456789" />
              </FormField>
              {/* CREATE ONLY. PATCH rejects `regulation` — changing which regime an
                  existing declarant is under reinterprets every record it already
                  owns — so unlike EORI above, this field does not survive into edit.
                  Required, and deliberately without a preselected value: the mongoose
                  field defaults to 'EU', so a blank submit would file the declarant
                  under the EU regime without anyone choosing it. */}
              {!isEdit ? (
                <FormField
                  name="regulation"
                  label="Regulation"
                  required
                  colSpan={6}
                >
                  <SelectField
                    options={REGULATIONS}
                    placeholder="Select a regime"
                  />
                </FormField>
              ) : null}
            </>
          ) : null}
        </FormSection>

        {/* A verifier firm's address is Annex §1.3(b), entered by its own Admin at setup. */}
        {isVerifier ? null : (
          <FormSection title="Address">
            <FormField
              name="address.city"
              label="City"
              required
              colSpan={6}
            >
              <TextField />
            </FormField>
            <FormField
              name="address.country"
              label="Country"
              required
              colSpan={6}
            >
              {/* `id`, not `iso2`. The API stores address.country as the numeric country
                _id and returns it that way, while an iso2-keyed field builds its
                options from `c.iso2` — so an existing address matched no option and
                the edit dialog opened with Country blank, silently demanding it be
                re-picked. `zCountry` accepts a string or a number and resolves either
                through countryFilter, so the id round-trips in both directions. */}
              <CountrySelectField valueAs="id" />
            </FormField>
            <FormField
              name="address.street"
              label="Street"
              colSpan={6}
            >
              <TextField />
            </FormField>
            <FormField
              name="address.number"
              label="Number"
              colSpan={3}
            >
              <TextField />
            </FormField>
            <FormField
              name="address.postcode"
              label="Postcode"
              colSpan={3}
            >
              <TextField />
            </FormField>
            <FormField
              name="address.state"
              label="State / region"
              colSpan={6}
            >
              <TextField />
            </FormField>
          </FormSection>
        )}

        {/* Consultants have no plans at all, so the whole section is absent for
            them rather than present-and-disabled. */}
        {!isEdit && !isPlanless ? (
          <FormSection
            title="Initial plan"
            description="Leave the plan unset to start this organization on its trial plan."
          >
            <FormField
              name="planId"
              label="Plan"
              colSpan={6}
              loading={plansQuery.isFetching}
            >
              <SelectField
                options={planOptions}
                placeholder={plansQuery.isFetching ? 'Loading plans…' : 'Trial plan (default)'}
              />
            </FormField>
            <FormField
              name="planExpiresAt"
              label="Expires on"
              colSpan={3}
            >
              <TextField type="date" />
            </FormField>
            <FormField
              name="planNeverExpires"
              label=" "
              colSpan={3}
            >
              <CheckboxField inlineLabel="Never expires" />
            </FormField>
          </FormSection>
        ) : null}

        <TypeWatcher onChange={handleTypeChange} />
      </Form>
    </CgDialog>
  );
}

/**
 * Bridges the form's `type` value out to component state.
 *
 * A child component rather than a `watch()` in the parent: the parent RENDERS the
 * <Form> that owns the react-hook-form context, so it is outside that context and
 * cannot subscribe to it. Rendering nothing, it costs one subscription.
 */
function TypeWatcher({ onChange }) {
  const value = useWatch({ name: 'type' });
  useEffect(() => {
    if (value) onChange(value);
  }, [value, onChange]);
  return null;
}
